import { useState, useRef, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { INITIAL_SHIPMENTS } from './preview/mockData'
import { autoName } from './preview/helpers'
import { NavSidebar } from './preview/Sidebar'
import Header, { NewShipmentFormBar } from './preview/Header'
import ActivityLog, { ProviderSelectionPanel, InputBar } from './preview/ActivityLog'
import ShipmentRail from './preview/ShipmentRail'
import './V2Workspace.css'

/* ====================================================================
   PAGE
   ==================================================================== */

export default function V2WorkspacePreview() {
  const [shipments, setShipments] = useState(INITIAL_SHIPMENTS)
  // The shipments list now lives at /preview/shipments; rows link here with
  // ?id=. With no id, open the Decide shipment so the comparison table shows.
  const [searchParams] = useSearchParams()
  const [activeId, setActiveId] = useState(() => {
    const requested = searchParams.get('id')
    if (requested && INITIAL_SHIPMENTS.some(s => s.id === requested)) return requested
    return INITIAL_SHIPMENTS.find(s => s.status === 'DECIDE')?.id || INITIAL_SHIPMENTS[0].id
  })
  const [filter, setFilter] = useState('All')
  const [input, setInput] = useState('')
  // Icon-only global nav by default — quick switching between shipments lives
  // in the breadcrumb dropdown (Header → ShipmentSwitcher), so the dark nav and
  // the workspace stay compact with no permanent list column.
  const [navCollapsed, setNavCollapsed] = useState(true)
  // Detail panel is a drawer — open when deciding, collapsible to a thin tab
  // when the user wants the chat to breathe (point 2 of the nav redesign).
  const [railOpen, setRailOpen] = useState(true)
  const [draftFormBar, setDraftFormBar] = useState({ origin: '', destination: '', mode: '', readyDate: '' })
  const [replayingId, setReplayingId] = useState(null)
  // Marq is "working" (animated mark) when the active shipment is streaming
  // events from either Send or Replay. Otherwise the mark is static.
  const [streamingId, setStreamingId] = useState(null)
  // Shimmering "thought" line beside the mark during streaming
  const [currentThought, setCurrentThought] = useState(null)
  // Per-message typing animation state: { msgId: revealedCharCount }
  const [typingTextById, setTypingTextById] = useState({})
  // Chat-driven share (MVP): when set, Marq has asked who to send the selected
  // quotes to and is waiting for the next message to be the recipient email(s).
  const [shareAwaiting, setShareAwaiting] = useState(null)

  const active = shipments.find(s => s.id === activeId)
  const isNewDraft = active?.isDraft === true

  const updateActive = (mutator) => {
    setShipments(prev => prev.map(s => s.id === activeId ? mutator(s) : s))
  }

  // Preview-only Accept: mark the chosen provider selected and flip the
  // shipment to BOOKED so the comparison table reflects the decision (mirrors
  // handleAcceptQuote in V2WorkspacePage, minus the API round-trip).
  const handleAcceptQuote = ({ quoteId, provider }) => {
    updateActive(s => ({
      ...s,
      status: 'BOOKED',
      providers: (s.providers || []).map(p =>
        p.quoteId === quoteId ? { ...p, status: 'selected' } : p
      ),
      activity: [
        ...s.activity,
        { id: `sys-accept-${quoteId}`, kind: 'system',
          text: `Accepted ${provider} · ${new Date().toLocaleString()}` }
      ]
    }))
  }

  // Dispatch curated the table and hit "Share" — hand off to Marq in the chat.
  // MVP: the share is chat-driven (no recipient-picker modal). Marq asks who to
  // send to; the user's next message is the recipient (handled in handleSend).
  // The real send/log/persist is backend (Richard); here we simulate it.
  const handleShare = (payload) => {
    const quotes = payload?.selected || []
    if (quotes.length === 0) return
    const n = quotes.length
    setShareAwaiting({ quotes })
    updateActive(s => ({
      ...s,
      activity: [
        ...s.activity,
        { id: `m-share-req-${Date.now()}`, kind: 'message', role: 'customer', author: 'You',
          text: `Share these ${n} quote${n === 1 ? '' : 's'} by email.` },
        { id: `m-share-ask-${Date.now() + 1}`, kind: 'message', role: 'ai', author: 'Marq',
          text: `Sure — who should I send these ${n} option${n === 1 ? '' : 's'} to? Reply with the recipient's email (you can list more than one).` }
      ]
    }))
  }

  const handleFieldUpdate = (section, fieldKey, newValue, opts = {}) => {
    const { notify = false } = opts
    updateActive(s => {
      const stamp = new Date().toLocaleString()
      const providerCount = (s.providers || []).filter(p => p.status === 'received' || p.status === 'pending').length
      const sectionLabel = { route: 'Route', cargo: 'Cargo', commodity: 'Commodity', commercial: 'Commercial' }[section]
      const fieldLabel = {
        originName: 'origin', destName: 'destination', mode: 'mode', readyDate: 'ready date',
        palletCount: 'pallet count', dims: 'dimensions', totalWeight: 'weight',
        description: 'description',
        incoterm: 'incoterm'
      }[fieldKey] || fieldKey
      const eventText = notify
        ? `${sectionLabel} ${fieldLabel} updated · ${providerCount} provider${providerCount === 1 ? '' : 's'} re-notified for requote · ${stamp}`
        : `${sectionLabel} ${fieldLabel} updated · ${stamp}`
      const newEvent = { id: `sys-${Date.now()}`, kind: 'system', text: eventText }

      const next = { ...s, activity: [...s.activity, newEvent] }
      if (section === 'route') {
        if (fieldKey === 'originName') next.route = { ...s.route, origin: { ...s.route.origin, name: newValue } }
        else if (fieldKey === 'destName') next.route = { ...s.route, destination: { ...s.route.destination, name: newValue } }
        else if (fieldKey === 'mode') next.mode = newValue
        else if (fieldKey === 'readyDate') next.readyDate = newValue
      } else if (section === 'cargo') {
        next.cargo = { ...s.cargo, [fieldKey]: newValue }
      } else if (section === 'commodity') {
        next.commodity = { ...s.commodity, [fieldKey]: newValue }
      } else if (section === 'commercial') {
        next.commercial = { ...s.commercial, [fieldKey]: newValue }
      }
      return next
    })
  }

  // In production: POST /api/v2/providers with newCandidate._formPayload, then re-fetch
  // suggestions. Here we just append to the activity item's candidates list.
  const handleAddManualProvider = (selectionItemId, newCandidate) => {
    updateActive(s => ({
      ...s,
      activity: s.activity.map(a =>
        a.id === selectionItemId && a.kind === 'provider_selection'
          ? { ...a, candidates: [...a.candidates, newCandidate] }
          : a
      )
    }))
  }

  // ──────────────────────────────────────────────────────────────────────────
  // Timer registry — every setTimeout / setInterval used by streaming, replay,
  // typing animation, and thought cycling is registered here so we can cancel
  // them all at once when the user switches shipments mid-stream.
  // ──────────────────────────────────────────────────────────────────────────
  const activeTimersRef = useRef(new Set())

  const scheduleTimeout = useCallback((cb, ms) => {
    const handle = setTimeout(() => {
      activeTimersRef.current.delete(handle)
      cb()
    }, ms)
    activeTimersRef.current.add(handle)
    return handle
  }, [])

  const scheduleInterval = useCallback((cb, ms) => {
    const handle = setInterval(cb, ms)
    activeTimersRef.current.add(handle)
    return handle
  }, [])

  const clearTrackedTimer = useCallback((handle) => {
    if (activeTimersRef.current.has(handle)) {
      clearTimeout(handle)
      clearInterval(handle)
      activeTimersRef.current.delete(handle)
    }
  }, [])

  const clearAllTimers = useCallback(() => {
    for (const handle of activeTimersRef.current) {
      clearTimeout(handle)
      clearInterval(handle)
    }
    activeTimersRef.current.clear()
  }, [])

  // Component-unmount cleanup
  useEffect(() => () => clearAllTimers(), [clearAllTimers])

  // Cycle thought lines while streaming
  useEffect(() => {
    if (!streamingId) {
      setCurrentThought(null)
      return
    }
    const thoughts = [
      'Drafting reply…',
      'Pulling lane history…',
      'Comparing all-in cost…',
      'Reviewing past patterns…',
      'Cross-checking provider response times…',
      'Normalizing quote terms…'
    ]
    let i = Math.floor(Math.random() * thoughts.length)
    setCurrentThought(thoughts[i])
    const handle = scheduleInterval(() => {
      i = (i + 1) % thoughts.length
      setCurrentThought(thoughts[i])
    }, 2000)
    return () => { clearTrackedTimer(handle); setCurrentThought(null) }
  }, [streamingId, scheduleInterval, clearTrackedTimer])

  // Type-out an AI message character by character
  const typeOutMessage = (msgId, fullText) => {
    setTypingTextById(prev => ({ ...prev, [msgId]: '' }))
    const charsPerTick = Math.max(1, Math.ceil(fullText.length / 60))
    const tickMs = 28
    const handle = scheduleInterval(() => {
      setTypingTextById(prev => {
        const cur = prev[msgId]
        if (cur === undefined) { clearTrackedTimer(handle); return prev }
        const nextLen = cur.length + charsPerTick
        if (nextLen >= fullText.length) {
          clearTrackedTimer(handle)
          const { [msgId]: _, ...rest } = prev
          return rest
        }
        return { ...prev, [msgId]: fullText.slice(0, nextLen) }
      })
    }, tickMs)
  }

  // Replay: clear activity and re-stream events with realistic delays
  const handleReplay = () => {
    if (!active || replayingId) return
    const shipmentId = active.id
    const events = [...active.activity]
    setReplayingId(shipmentId)
    setStreamingId(shipmentId)
    setShipments(prev => prev.map(s => s.id === shipmentId ? { ...s, activity: [] } : s))

    let cumulative = 0
    events.forEach((ev, i) => {
      const stepDelay =
        ev.kind === 'system' ? 750 :
        ev.kind === 'message' && ev.role === 'ai' ? 2400 :
        ev.kind === 'message' && ev.role === 'customer' ? 1800 :
        ev.kind === 'quote' ? 1500 :
        ev.kind === 'compare' ? 2800 :
        ev.kind === 'booking' ? 1500 :
        ev.kind === 'provider_selection' ? 1200 :
        1000
      cumulative += stepDelay
      const isLast = i === events.length - 1
      scheduleTimeout(() => {
        if (replayingId !== null && replayingId !== shipmentId) return
        setShipments(prev => prev.map(s =>
          s.id === shipmentId ? { ...s, activity: [...s.activity, ev] } : s
        ))
        if (ev.kind === 'message' && ev.role === 'ai' && ev.text) {
          typeOutMessage(ev.id, ev.text)
        }
        if (isLast) {
          setReplayingId(null)
          scheduleTimeout(() => setStreamingId(null), 2000)
        }
      }, cumulative)
    })
    if (events.length === 0) { setReplayingId(null); setStreamingId(null) }
  }

  const appendActivity = (shipmentId, ...events) => {
    setShipments(prev => prev.map(s =>
      s.id === shipmentId
        ? { ...s, activity: [...s.activity, ...events] }
        : s
    ))
  }
  const replaceActivityItem = (shipmentId, itemId, replacement) => {
    setShipments(prev => prev.map(s =>
      s.id === shipmentId
        ? { ...s, activity: s.activity.map(a => a.id === itemId ? replacement : a) }
        : s
    ))
  }

  const handleDismissSelection = (selectionItemId) => {
    updateActive(s => ({
      ...s,
      activity: [
        ...s.activity.map(a => a.id === selectionItemId ? { ...a, dismissed: true } : a),
        { id: `m-marq-pause-${Date.now()}`, kind: 'message', role: 'ai', author: 'Marq',
          text: "No problem — keep going. When you're ready, just say \"send quotes\" or tell me which providers and I'll bring the picker back." }
      ]
    }))
  }

  const handleRecallSelection = () => {
    updateActive(s => ({
      ...s,
      activity: s.activity.map(a =>
        a.kind === 'provider_selection' ? { ...a, dismissed: false } : a
      )
    }))
  }

  const handleSendQuotes = (selectionItemId, providerIds) => {
    const shipmentId = activeId
    const shipment = shipments.find(s => s.id === shipmentId)
    if (!shipment) return

    const candidates = shipment.activity.find(a => a.id === selectionItemId)?.candidates || []
    const chosen = providerIds.map(id => {
      const c = candidates.find(x => x.providerId === id)
      return c ? { name: c.name, status: 'pending' } : null
    }).filter(Boolean)

    setStreamingId(shipmentId)
    replaceActivityItem(shipmentId, selectionItemId, {
      id: `m-marq-start-${Date.now()}`,
      kind: 'message',
      role: 'ai',
      author: 'Marq',
      text: `Sending quote requests to ${chosen.length} provider${chosen.length === 1 ? '' : 's'} — ${chosen.map(c => c.name).join(', ')}.`
    })
    setShipments(prev => prev.map(s =>
      s.id === shipmentId ? { ...s, providers: chosen, status: 'RFQS_SENT' } : s
    ))

    const baseDelay = 1100
    chosen.forEach((c, i) => {
      const time = new Date(Date.now() + i * baseDelay)
      const stamp = time.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', second: '2-digit' })
      scheduleTimeout(() => {
        appendActivity(shipmentId, {
          id: `sys-rfq-${Date.now()}-${i}`,
          kind: 'system',
          text: `RFQ sent to ${c.name} · ${stamp}`
        })
      }, (i + 1) * baseDelay)
    })

    scheduleTimeout(() => {
      const closingId = `m-marq-watching-${Date.now()}`
      const closingText = `Done. I'll watch for responses — typical wait on this lane is 4–12 hours. I'll parse them, build a comparison, and surface my recommendation here when at least 2 are in.`
      appendActivity(shipmentId, {
        id: closingId,
        kind: 'message',
        role: 'ai',
        author: 'Marq',
        text: closingText
      })
      typeOutMessage(closingId, closingText)
      scheduleTimeout(() => setStreamingId(null), 4500)
    }, (chosen.length + 1) * baseDelay)
  }

  const handleSend = () => {
    if (!input.trim()) return
    const text = input.trim()
    const userMsg = {
      id: `m-${Date.now()}`,
      kind: 'message',
      role: 'customer',
      author: 'You',
      text
    }
    setInput('')

    // Chat-driven share: Marq asked who to send the selected quotes to — this
    // message is the recipient. Simulate the send (Company + price only, no
    // carrier contact, no follow-up) and log it. Real send is backend (Richard).
    if (shareAwaiting) {
      const { quotes } = shareAwaiting
      const lines = quotes
        .map(q => `• ${q.name} — ${q.cost != null ? `$${Number(q.cost).toLocaleString()}` : '—'}`)
        .join('\n')
      const n = quotes.length
      updateActive(s => ({
        ...s,
        activity: [
          ...s.activity,
          userMsg,
          { id: `m-share-done-${Date.now() + 1}`, kind: 'message', role: 'ai', author: 'Marq',
            text: `Done — I emailed ${text} a text summary of the ${n} selected option${n === 1 ? '' : 's'}:\n\n${lines}\n\nCompany and price only — no carrier contact info, and no follow-up will be sent.` },
          { id: `sys-share-log-${Date.now() + 2}`, kind: 'system',
            text: `Shared ${n} quote${n === 1 ? '' : 's'} with ${text} · ${new Date().toLocaleString()}` }
        ]
      }))
      setShareAwaiting(null)
      return
    }

    const dismissedSelection = active?.activity.find(a => a.kind === 'provider_selection' && a.dismissed)
    const lower = text.toLowerCase()
    const readyTrigger = /\b(ready|send (the )?(quote|quotes|rfq|rfqs)|let'?s (go|send|do it))\b/.test(lower)
    const namedProvider = dismissedSelection?.candidates.some(c => lower.includes(c.name.toLowerCase()))

    if (dismissedSelection && (readyTrigger || namedProvider)) {
      const reason = readyTrigger ? "Got it — pulling the picker back up." : "Got it — I see those providers on your list. Confirm below and I'll send."
      const aiMsg = {
        id: `m-marq-recall-${Date.now()}`,
        kind: 'message',
        role: 'ai',
        author: 'Marq',
        text: reason
      }
      updateActive(s => ({
        ...s,
        activity: [
          ...s.activity.map(a =>
            a.id === dismissedSelection.id ? { ...a, dismissed: false } : a
          ),
          userMsg,
          aiMsg
        ]
      }))
      return
    }

    const aiMsg = {
      id: `m-${Date.now() + 1}`,
      kind: 'message',
      role: 'ai',
      author: 'Marq',
      text: "Got it — I've noted that. (Preview mode: AI replies are mocked. In production this would route to the live quoting agent.)"
    }
    updateActive(s => ({ ...s, activity: [...s.activity, userMsg, aiMsg] }))
  }

  const handleRename = (shipmentId, newName) => {
    setShipments(prev => prev.map(s =>
      s.id === shipmentId ? { ...s, userName: newName } : s
    ))
  }

  const switchToShipment = (newId) => {
    if (newId === activeId) return
    clearAllTimers()
    setStreamingId(null)
    setReplayingId(null)
    setTypingTextById({})
    setCurrentThought(null)
    setActiveId(newId)
  }

  const handleNew = () => {
    const draftId = `DRAFT-${Date.now()}`
    const draft = {
      id: draftId,
      isDraft: true,
      status: 'DRAFT',
      needsAttention: false,
      createdAt: new Date().toISOString(),
      createdBy: 'aaron@marteu.com',
      route: { origin: { code: '—', name: '—' }, destination: { code: '—', name: '—' } },
      mode: '—',
      cargo: {},
      commercial: {},
      commodity: {},
      readyDate: '',
      activity: [{
        id: 'm0', kind: 'message', role: 'ai', author: 'Marq',
        text: "Hi — to start a new shipment, fill in the form bar above with origin, destination, mode, and ready date. Once you start, I'll ask follow-ups for cargo, commodity, and incoterm right here. I'll auto-name the shipment in the sidebar — click the name any time to rename it."
      }],
      providers: [],
      timeline: [{ date: new Date().toLocaleDateString('en-US', { month: 'short', day: 'numeric' }), label: 'Created', current: true }]
    }
    setShipments(prev => [draft, ...prev])
    switchToShipment(draftId)
    setDraftFormBar({ origin: '', destination: '', mode: '', readyDate: '' })
  }

  const handleStartFromForm = () => {
    if (!draftFormBar.origin || !draftFormBar.destination || !draftFormBar.mode) return
    const [origCode, ...origRest] = draftFormBar.origin.split(' ')
    const [destCode, ...destRest] = draftFormBar.destination.split(' ')
    updateActive(s => ({
      ...s,
      isDraft: false,
      route: {
        origin: { code: origCode || draftFormBar.origin.slice(0,5).toUpperCase(), name: draftFormBar.origin },
        destination: { code: destCode || draftFormBar.destination.slice(0,5).toUpperCase(), name: draftFormBar.destination }
      },
      mode: draftFormBar.mode,
      readyDate: draftFormBar.readyDate || s.readyDate,
      activity: [
        ...s.activity,
        { id: `sys-${Date.now()}`, kind: 'system', text: `Anchors set: ${draftFormBar.origin} → ${draftFormBar.destination}, ${draftFormBar.mode}` },
        { id: `m-${Date.now()+1}`, kind: 'message', role: 'ai', author: 'Marq',
          text: `Got it — ${draftFormBar.origin} to ${draftFormBar.destination} via ${draftFormBar.mode}. What's the cargo? Pallet count, dimensions, and weight will let me request quotes.` }
      ]
    }))
  }

  const showFormBar = isNewDraft

  return (
    <div className="ws-app">
      <NavSidebar collapsed={navCollapsed} onToggle={() => setNavCollapsed(c => !c)} />

      <div className="ws-grid">
        <div className="ws-main">
          {active ? (
            <>
              <Header
                shipment={active}
                allShipments={shipments}
                onReplay={handleReplay}
                replaying={replayingId === active.id}
                onRename={handleRename}
                backTo="/preview/shipments"
                onSelect={switchToShipment}
                onNew={handleNew}
                activeId={activeId}
              />
              {showFormBar && (
                <NewShipmentFormBar
                  draft={draftFormBar}
                  onChange={setDraftFormBar}
                  onStart={handleStartFromForm}
                />
              )}
              {(() => {
                const pendingSelection = active.activity.find(
                  a => a.kind === 'provider_selection' && !a.dismissed
                )
                return (
                  <>
                    <ActivityLog
                      shipment={active}
                      onSendQuotes={handleSendQuotes}
                      onAddManualProvider={handleAddManualProvider}
                      onAcceptQuote={handleAcceptQuote}
                      onShare={handleShare}
                      isStreaming={streamingId === active.id}
                      showPresence={!pendingSelection}
                      currentThought={streamingId === active.id ? currentThought : null}
                      typingTextById={typingTextById}
                    />
                    {pendingSelection ? (
                      <ProviderSelectionPanel
                        variant="footer"
                        item={pendingSelection}
                        onSend={ids => handleSendQuotes(pendingSelection.id, ids)}
                        onAddManual={handleAddManualProvider}
                        onDismiss={() => handleDismissSelection(pendingSelection.id)}
                      />
                    ) : (
                      <InputBar
                        value={input}
                        onChange={setInput}
                        onSend={handleSend}
                        disabled={isNewDraft && !draftFormBar.origin}
                      />
                    )}
                  </>
                )
              })()}
            </>
          ) : (
            <div style={{ padding: 40, color: 'var(--ws-text-muted)' }}>
              Select a shipment from the sidebar.
            </div>
          )}
        </div>

        {active && (
          <div className={`ws-detail ${railOpen ? 'is-open' : 'is-collapsed'}`}>
            {railOpen ? (
              <>
                <button
                  type="button"
                  className="ws-detail-collapse"
                  onClick={() => setRailOpen(false)}
                  title="Collapse detail"
                  aria-label="Collapse detail panel"
                >
                  <ChevronRight size={16} strokeWidth={2} />
                </button>
                <ShipmentRail shipment={active} onFieldUpdate={handleFieldUpdate} />
              </>
            ) : (
              <button
                type="button"
                className="ws-detail-tab"
                onClick={() => setRailOpen(true)}
                title="Open detail"
                aria-label="Open detail panel"
              >
                <ChevronLeft size={14} strokeWidth={2} />
                <span className="ws-detail-tab-text">Detail</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
